import { useEffect, useState } from 'react';
import { WifiOff, Wifi, RefreshCw } from 'lucide-react';
import { useOfflineStatus } from '@/hooks/useOfflineStatus';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

export function OfflineIndicator() {
  const { isOffline, wasOffline, clearWasOffline } = useOfflineStatus();
  const [showReconnected, setShowReconnected] = useState(false);

  useEffect(() => {
    if (wasOffline && !isOffline) {
      setShowReconnected(true);
      const timer = setTimeout(() => {
        setShowReconnected(false);
        clearWasOffline();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [wasOffline, isOffline, clearWasOffline]);

  return (
    <AnimatePresence>
      {isOffline && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className={cn(
            "fixed top-0 left-0 right-0 z-[100]",
            "bg-amber-500/95 dark:bg-amber-600/95 backdrop-blur-sm",
            "text-white py-2 px-4",
            "flex items-center justify-center gap-2",
            "text-sm font-medium shadow-lg"
          )}
        >
          <WifiOff className="h-4 w-4" />
          <span>You're offline — showing cached data</span>
        </motion.div>
      )}

      {showReconnected && !isOffline && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className={cn(
            "fixed top-0 left-0 right-0 z-[100]",
            "bg-emerald-500/95 dark:bg-emerald-600/95 backdrop-blur-sm",
            "text-white py-2 px-4",
            "flex items-center justify-center gap-2",
            "text-sm font-medium shadow-lg"
          )}
        >
          <Wifi className="h-4 w-4" />
          <span>Back online</span>
          <RefreshCw className="h-3 w-3 animate-spin ml-1" />
          <span className="text-white/80">Syncing...</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
